import React from 'react';
import { Loader2, Building2, Calendar, ArrowUpCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type Props = {
  passportNumber: string;
  setPassportNumber: (value: string) => void;
  selectedPilgrim: any;
  selectedHotel: string;
  setSelectedHotel: (value: string) => void;
  targetRoomType: 'double' | 'triple' | null;
  setTargetRoomType: (value: 'double' | 'triple' | null) => void;
  isSearching: boolean;
  isSubmitting: boolean;
  onSearch: () => void;
  onSubmit: () => void;
  errorMessage: string | null;
};

type HotelOption = {
  value: string;
  label: string;
  currentType: string;
  startDate: string;
  endDate: string;
  type: 'main' | 'shifting' | 'madinah';
};

export const RequestForm = ({
  passportNumber,
  setPassportNumber,
  selectedPilgrim,
  selectedHotel,
  setSelectedHotel,
  targetRoomType,
  setTargetRoomType,
  isSearching,
  isSubmitting,
  onSearch,
  onSubmit,
  errorMessage
}: Props) => {
  const { translations } = useLanguage();

  // Get eligible hotels based on room types
  const getEligibleHotels = (): HotelOption[] => {
    if (!selectedPilgrim?.package) return [];

    const pkg = selectedPilgrim.package;
    const options: HotelOption[] = [];

    // Add main Makkah hotel if eligible
    if (selectedPilgrim.main_makkah_roomType && 
        ['triple', 'quadruple'].includes(selectedPilgrim.main_makkah_roomType || '')) {
      options.push({
        value: pkg.mainMakkahHotel,
        label: pkg.mainMakkahHotel,
        currentType: selectedPilgrim.main_makkah_roomType || '',
        startDate: pkg.mainMakkahStartDate,
        endDate: pkg.mainMakkahEndDate,
        type: 'main'
      });
    }

    // Add shifting Makkah hotel if exists and eligible
    if (pkg.shiftingMakkahHotel && 
        selectedPilgrim.shifting_makkah_roomType && 
        ['triple', 'quadruple'].includes(selectedPilgrim.shifting_makkah_roomType || '')) {
      options.push({
        value: pkg.shiftingMakkahHotel,
        label: pkg.shiftingMakkahHotel,
        currentType: selectedPilgrim.shifting_makkah_roomType || '',
        startDate: pkg.shiftingMakkahStartDate,
        endDate: pkg.shiftingMakkahEndDate,
        type: 'shifting'
      });
    }

    // Add Madinah hotel if eligible
    if (selectedPilgrim.madinah_roomType && 
        ['triple', 'quadruple'].includes(selectedPilgrim.madinah_roomType || '')) {
      options.push({
        value: pkg.madinahHotel,
        label: pkg.madinahHotel,
        currentType: selectedPilgrim.madinah_roomType || '',
        startDate: pkg.madinahStartDate,
        endDate: pkg.madinahEndDate,
        type: 'madinah'
      });
    }

    return options;
  };

  const eligibleHotels = getEligibleHotels();

  // Get available room types for selected hotel
  const getAvailableRoomTypes = (hotel: string): ('double' | 'triple')[] => {
    const selectedHotelOption = eligibleHotels.find(h => h.value === hotel);
    if (!selectedHotelOption) return [];

    const currentType = selectedHotelOption.currentType || '';
    const types: ('double' | 'triple')[] = [];

    // Can upgrade to double if current room is triple or quadruple
    if (currentType === 'triple' || currentType === 'quadruple') {
      types.push('double');
    }

    // Can upgrade to triple only if current room is quadruple
    if (currentType === 'quadruple') {
      types.push('triple');
    }

    return types;
  };

  const handleUpgradeClick = (hotel: HotelOption, type: 'double' | 'triple') => {
    setSelectedHotel(hotel.value);
    setTargetRoomType(type);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
      <h2 className="text-lg font-semibold">
        {translations?.roomUpgrade?.createRequest || 'Create Upgrade Request'}
      </h2>

      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-50 p-4 rounded-lg text-red-700 border border-red-200">
          <p className="font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Passport Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.roomUpgrade?.passportNumber || 'Passport Number'}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              placeholder="Enter passport number..."
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={onSearch}
              disabled={isSearching || !passportNumber || passportNumber.trim() === ''}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                isSearching || !passportNumber || passportNumber.trim() === ''
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {isSearching ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                'Search'
              )}
            </button>
          </div>
        </div>

        {/* Selected Pilgrim Info */}
        {selectedPilgrim && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Selected Pilgrim</h3>
            <div className="text-sm text-gray-600">
              <p>Name: {selectedPilgrim.pilgrimName}</p>
              <p>Package: {selectedPilgrim.packageName}</p>
              <p>Room Types:</p>
              <ul className="list-disc list-inside ml-2">
                {selectedPilgrim.main_makkah_roomType && (
                  <li>Main Makkah: {selectedPilgrim.main_makkah_roomType}</li>
                )}
                {selectedPilgrim.shifting_makkah_roomType && (
                  <li>Shifting Makkah: {selectedPilgrim.shifting_makkah_roomType}</li>
                )}
                {selectedPilgrim.madinah_roomType && (
                  <li>Madinah: {selectedPilgrim.madinah_roomType}</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Hotel Selection */}
        {selectedPilgrim && (
          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              {translations?.roomUpgrade?.selectHotel || 'Select Hotel and Room Type for Upgrade'}
            </label>
            
            {eligibleHotels.length === 0 ? (
              <div className="bg-yellow-50 p-4 rounded-lg text-yellow-700">
                <p className="font-medium">No Hotels Eligible for Upgrade</p>
                <p className="text-sm mt-1">
                  Pilgrim must be in a triple or quadruple room to be eligible for upgrade.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {eligibleHotels.map((hotel) => (
                  <div
                    key={hotel.value}
                    className={`
                      relative rounded-xl overflow-hidden border-2 transition-all
                      ${selectedHotel === hotel.value 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-gray-200 bg-white'
                      }
                    `}
                  >
                    {/* Hotel Type Badge */}
                    <div className={`
                      absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium
                      ${hotel.type === 'main' 
                        ? 'bg-blue-100 text-blue-700' 
                        : hotel.type === 'shifting'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                      }
                    `}>
                      {hotel.type === 'main' 
                        ? 'Main Makkah'
                        : hotel.type === 'shifting'
                        ? 'Shifting Makkah'
                        : 'Madinah'
                      }
                    </div>

                    <div className="p-4 pt-12">
                      {/* Hotel Info */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className="p-2 bg-primary-100 rounded-lg shrink-0">
                          <Building2 className="text-primary-600" size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{hotel.label}</h4>
                          <p className="text-sm text-gray-500 mt-1">
                            Current Room: {hotel.currentType}
                          </p>
                        </div>
                      </div>

                      {/* Stay Period */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <Calendar size={14} />
                        <div>
                          <div>{hotel.startDate}</div>
                          <div>{hotel.endDate}</div>
                        </div>
                      </div>

                      {/* Available Upgrades */}
                      <div className="grid grid-cols-1 gap-2">
                        {getAvailableRoomTypes(hotel.value).map(type => (
                          <motion.button
                            key={type}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleUpgradeClick(hotel, type)}
                            className={`
                              flex items-center justify-center gap-2 p-3 rounded-lg w-full
                              transition-colors font-medium
                              ${selectedHotel === hotel.value && targetRoomType === type
                                ? 'bg-primary-500 text-white'
                                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                              }
                            `}
                          >
                            <ArrowUpCircle size={18} />
                            <span>Upgrade to {type} Room</span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onSubmit}
          disabled={isSubmitting || !selectedPilgrim || !selectedHotel || !targetRoomType}
          className={`px-6 py-2 rounded-lg text-white transition-colors ${
            isSubmitting || !selectedPilgrim || !selectedHotel || !targetRoomType
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {isSubmitting
            ? translations?.roomUpgrade?.submitting || 'Submitting...'
            : translations?.roomUpgrade?.submit || 'Submit Request'}
        </button>
      </div>
    </div>
  );
};